export const FIELD_KEYS = [
  'sequence',
  'code',
  'name',
  'description',
  'unit',
  'quantity',
  'unitPrice',
  'amount',
] as const;

export type FieldKey = (typeof FIELD_KEYS)[number];

export const RULE_IDS = [
  'BQ001',
  'BQ002',
  'BQ003',
  'BQ004',
  'BQ005',
  'BQ006',
  'BQ007',
  'BQ008',
  'BQ009',
  'BQ010',
  'BQ011',
  'BQ012',
  'BQ013',
  'BQ014',
  'BQ015',
  'BQ016',
] as const;

export type RuleId = (typeof RULE_IDS)[number];

export const SEVERITIES = ['error', 'warning', 'info'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const ROW_KINDS = ['detail', 'title', 'subtotal', 'note', 'blank'] as const;
export type RowKind = (typeof ROW_KINDS)[number];

export type RowClassificationReason =
  | 'all-fields-empty'
  | 'subtotal-keyword'
  | 'note-keyword'
  | 'narrative-without-detail-signals'
  | 'heading-without-detail-signals'
  | 'description-pair'
  | 'code-present'
  | 'unit-present'
  | 'numeric-field-present';

export interface RowClassification {
  readonly kind: RowKind;
  readonly reason: RowClassificationReason;
}

/** A parser-neutral representation of one mapped Excel cell. */
export interface BoQCell {
  /** Cached/evaluated value. Formula cells without a cached result use null. */
  readonly value: unknown;
  /** Display text retained for diagnostics and exports. */
  readonly text: string;
  /** Formula without the leading '='; null for non-formula cells. */
  readonly formula: string | null;
  readonly formulaResultAvailable: boolean;
}

export type FieldCells = Readonly<Partial<Record<FieldKey, BoQCell>>>;

/** One-based Excel column numbers keyed by semantic field. */
export type FieldMapping = Readonly<Partial<Record<FieldKey, number>>>;

export interface SheetMapping {
  readonly sheetName: string;
  /** One-based Excel row number. */
  readonly headerRow: number;
  readonly fields: FieldMapping;
  readonly confidence: number;
  readonly confirmed: boolean;
}

export interface BoQRow {
  readonly sheetName: string;
  /** One-based row number from the original workbook. */
  readonly excelRow: number;
  readonly cells: FieldCells;
  readonly classification: RowClassification;
}

export interface WorksheetData {
  readonly name: string;
  readonly headerRow: number;
  readonly mapping: SheetMapping;
  readonly rows: readonly BoQRow[];
}

export interface WorkbookData {
  readonly fileName: string;
  readonly fileSize: number;
  readonly sheets: readonly WorksheetData[];
}

export interface HeaderAliasMatch {
  readonly field: FieldKey;
  readonly column: number;
  readonly header: string;
  readonly alias: string;
  readonly confidence: number;
}

export interface HeaderRowCandidate {
  /** One-based Excel row number. */
  readonly rowNumber: number;
  readonly score: number;
  readonly confidence: number;
  readonly matches: readonly HeaderAliasMatch[];
}

export interface AmountToleranceSettings {
  /** Decimal currency amount, represented as text to avoid binary rounding. */
  readonly absolute: string;
  /** Fraction (0.0001 = 0.01%), represented as text. */
  readonly relative: string;
}

export interface InspectionSettings {
  /** Missing entries fall back to the rule's default enabled state. */
  readonly enabledRules: Readonly<Partial<Record<RuleId, boolean>>>;
  readonly amountTolerance: AmountToleranceSettings;
  /** Fraction (0.5 = 50%), represented as text. */
  readonly priceDeviationThreshold: string;
  readonly minimumDescriptionLength: number;
}

export interface Issue {
  readonly id: string;
  readonly severity: Severity;
  readonly ruleId: RuleId;
  readonly ruleName: string;
  readonly sheetName: string;
  readonly excelRow: number;
  readonly code: string;
  readonly name: string;
  readonly field: FieldKey | null;
  readonly currentValue: string;
  readonly message: string;
  readonly suggestion: string;
  readonly relatedRows: readonly number[];
}

export interface RuleFinding {
  readonly row: BoQRow;
  readonly field: FieldKey | null;
  readonly currentValue?: string;
  readonly message?: string;
  readonly suggestion?: string;
  readonly relatedRows?: readonly number[];
}

export interface RuleContext {
  readonly sheet: WorksheetData;
  readonly detailRows: readonly BoQRow[];
  readonly settings: InspectionSettings;
}

export interface RuleDefinition {
  readonly id: RuleId;
  readonly name: string;
  readonly description: string;
  readonly defaultSeverity: Severity;
  readonly enabled: boolean;
  readonly applicableFields: readonly FieldKey[];
  readonly check: (context: RuleContext) => readonly RuleFinding[];
  readonly message: string;
  readonly suggestion: string;
}

export interface InspectionSummary {
  readonly sheetCount: number;
  readonly detailRowCount: number;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly infoCount: number;
}

export interface InspectionResult {
  readonly issues: readonly Issue[];
  readonly summary: InspectionSummary;
  readonly enabledRuleIds: readonly RuleId[];
}
